import linkMap from "@/common/linkMap.json";
import { useRouter } from "next/router";
import { defaultLanguage, language, Language } from "./config";
import { publicEnv } from "./publicEnv";

/**
 * Get the link for a given key and locale
 * @param key - The key of the link
 * @param locale - The locale of the link
 * @returns The link for the given key and locale, example: /en/projects /projectek
 */
export const getLink = (
  key: keyof typeof linkMap,
  locale: Language
): string => {
  const links = linkMap[key];
  if (!links) {
    throw new Error(`Link ${key} not found`);
  }

  return locale === defaultLanguage
    ? links[locale]
    : links[locale].startsWith(`/${locale}`)
      ? links[locale]
      : `/${locale}${links[locale]}`;
};

/**
 * Get the alternate links for a given key, for example: (en) /en/projects, (hu) /projectek
 * @param key - The key of the link
 * @returns The alternate links for the given key
 */
export const getAlternateLinks = (key: keyof typeof linkMap) => {
  return Object.entries(linkMap[key]).map(([locale, _]) => ({
    hrefLang: locale,
    href: getLink(key, locale as Language),
  }));
};

/**
 * Normalize pathname by removing locale prefix
 * @param cleanPathname - The clean pathname (without query params or hash)
 * @param currentLocale - The current locale
 * @returns The normalized pathname without locale prefix
 */
const getNormalizedPathname = (
  cleanPathname: string,
  currentLocale: Language
): string => {
  if (cleanPathname.startsWith(`/${currentLocale}/`)) {
    return cleanPathname.slice(`/${currentLocale}`.length);
  }
  if (cleanPathname === `/${currentLocale}`) {
    return "/";
  }
  return cleanPathname;
};

/**
 * Check if a pathname matches a slug pattern
 * @param pattern - The slug pattern (e.g., "/articles/:slug")
 * @param normalizedPath - The normalized pathname
 * @param cleanPathname - The original clean pathname
 * @returns True if the pathname matches the pattern
 */
const matchSlugPattern = (
  pattern: string,
  normalizedPath: string,
  cleanPathname: string
): boolean => {
  const regex = new RegExp(`^${pattern.replace(/:slug/g, "[^/]+")}$`);
  return regex.test(normalizedPath) || regex.test(cleanPathname);
};

/**
 * Get the link for the current page in a different language
 * @param pathname - The current pathname
 * @param currentLocale - The current locale
 * @param targetLocale - The target locale
 * @returns The link for the current page in the target locale
 */
export const getLinkForCurrentPage = (
  pathname: string,
  currentLocale: Language,
  targetLocale: Language
): string => {
  // Remove query params and hash
  const cleanPathname = pathname.split("?")[0].split("#")[0];

  // Remove locale prefix if present (e.g., "/en/projects" -> "/projects")
  const normalizedPath = getNormalizedPathname(cleanPathname, currentLocale);

  // Check if we're on a slug page (articles or projects)
  // If so, redirect to the root of that section
  const articlesSlugPattern = linkMap["page.articles.slug"][currentLocale];
  const projectsSlugPattern = linkMap["page.projects.slug"][currentLocale];

  if (matchSlugPattern(articlesSlugPattern, normalizedPath, cleanPathname)) {
    // Redirect to articles root
    return getLink("page.articles", targetLocale);
  }

  if (matchSlugPattern(projectsSlugPattern, normalizedPath, cleanPathname)) {
    // Redirect to projects root
    return getLink("page.projects", targetLocale);
  }

  // Find matching page key using Hungarian path as reference
  for (const [key, locales] of Object.entries(linkMap)) {
    // Skip slug pages
    if (key.includes(".slug")) {
      continue;
    }

    const currentPath = locales[currentLocale];
    // Check both normalized and original pathname
    if (currentPath === normalizedPath || currentPath === cleanPathname) {
      return getLink(key as keyof typeof linkMap, targetLocale);
    }
  }

  // Fallback to home page
  return getLink("page.home", targetLocale);
};

/**
 * Get the absolute link for a given link
 * @param link - The link to get the absolute link for
 * @returns The absolute link for the given link
 */
export const getAbsoluteLink = (link: string) => {
  // If already an absolute URL, return as-is
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }
  return `${publicEnv.NEXT_PUBLIC_SITE_URL}${link.startsWith("/") ? link : `/${link}`}`;
};

export const useLinks = () => {
  const { locale } = useRouter();
  const lang: Language = language.parse(locale) || defaultLanguage;
  return {
    /**
     * Get the link for a given key and language
     * @param key - The key of the link
     * @returns The link for the given key and language
     */
    getLink: (key: keyof typeof linkMap) => {
      return getLink(key, lang);
    },

    /**
     * Canonical link is the one true link to the page,
     * always https, and excluding query params for example.
     * It is different per locale.
     * @param key - The key of the link
     * @returns The canonical link for the given key
     */
    getCanonical: (key: keyof typeof linkMap) => {
      return getLink(key, lang);
    },

    /**
     * Canonical link for the default language
     * @param key - The key of the link
     * @returns The canonical link for the given key for the default language
     */
    getCanonicalXDefault: (key: keyof typeof linkMap) => {
      return getLink(key, defaultLanguage);
    },
  };
};
